import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedCastLibraryEntry } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { footageMessages } from '../../messages/footageMessages';
import { AI_CLIP_MAX_REFERENCES, libraryGroups, toggleReference } from './referenceDefaults';
/* eslint-disable css-modules/no-unused-class -- the editor stylesheet; this part uses its own slice of it */
import editorStyles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for these parts of the editor; each part uses its own slice of it */
import styles from './ReferencePicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReferencePickerProps {
  /** `castLibrary` of the editor answer: once per file, newest video first. */
  library: DirectedCastLibraryEntry[];
  /** The file ids that the form has now. */
  selected: number[];
  onDone: (fileIds: number[]) => void;
}

/**
 * The reference picker (directed video design i2): the cast of every directed video of the account,
 * grouped by song, at most 3 picks with their order on a tick. A fourth picture is off until one is
 * removed. "Done" gives the picks to the form; "Clear" removes them all.
 */
const ReferencePickerComponent: React.FC<ReferencePickerProps> = ({ library, selected, onDone }) => {
  const intl = useIntl();
  const [picks, setPicks] = useState<number[]>(selected);
  const groups = useMemo(() => libraryGroups(library), [library]);
  const isFull = picks.length >= AI_CLIP_MAX_REFERENCES;

  return (
    <div
      className={styles.popover}
      role="dialog"
      aria-label={intl.formatMessage(footageMessages.pickerTitle, { max: AI_CLIP_MAX_REFERENCES })}
    >
      <div className={styles.head}>
        <b>{intl.formatMessage(footageMessages.pickerTitle, { max: AI_CLIP_MAX_REFERENCES })}</b>
        <span className={styles.count}>
          {intl.formatMessage(footageMessages.referencesCount, { count: picks.length, max: AI_CLIP_MAX_REFERENCES })}
        </span>
      </div>
      {groups.map((group) => (
        <React.Fragment key={group.songTitle}>
          <span className={styles.source}>
            {intl.formatMessage(footageMessages.pickerSource, { title: group.songTitle })}
          </span>
          <div className={styles.grid}>
            {group.entries.map((entry) => {
              const order = picks.indexOf(entry.fileId);
              const isOn = order >= 0;
              const isOff = !isOn && isFull;
              return (
                <button
                  key={entry.fileId}
                  type="button"
                  className={clsx(styles.item, isOn && styles.itemOn, isOff && styles.itemOff)}
                  aria-pressed={isOn}
                  aria-disabled={isOff}
                  aria-label={entry.label}
                  onClick={() => setPicks((current) => toggleReference(current, entry.fileId))}
                >
                  <img className={styles.photo} src={entry.url} alt="" />
                  {isOn && <span className={styles.tick}>{order + 1}</span>}
                  <span className={styles.name}>{entry.label}</span>
                </button>
              );
            })}
          </div>
        </React.Fragment>
      ))}
      <p className={editorStyles.hint}>{intl.formatMessage(footageMessages.pickerHint)}</p>
      <div className={styles.footer}>
        <button type="button" className={editorStyles.linkButton} onClick={() => setPicks([])}>
          {intl.formatMessage(footageMessages.pickerClear)}
        </button>
        <SMLButton size="sm" omitCap onClick={() => onDone(picks)}>
          {intl.formatMessage(footageMessages.pickerDone)}
        </SMLButton>
      </div>
    </div>
  );
};

export const ReferencePicker = TypedMemo(ReferencePickerComponent);
